import { section, p, list, steps, tip, warn, table, faq } from './blocks';

export default {
  slug: 'reporting-controlling',
  title: 'Reporting & Controlling',
  shortTitle: 'Reporting & Controlling',
  icon: 'BarChart3',
  updated: 'Juni 2026',
  datePublished: '2026-06-24',
  dateModified: '2026-06-24',
  image: 'https://images.unsplash.com/photo-1763038311036-6d18805537e5?w=1200&h=900&fit=crop',
  teaser: 'Umsatz, Wareneinsatz, Personalkosten, Deckungsbeitrag: Wie du aus Kassendaten und Buchhaltung ein verständliches Reporting baust und Entscheidungen auf Zahlen statt Bauchgefühl stützt.',
  mainArticle: {
    title: 'Reporting und Controlling in der Gastronomie: Der große Leitfaden',
    takeaways: [
      'Umsatz kennen reicht nicht – Wareneinsatz, Personalkosten und Deckungsbeitrag entscheiden über den Gewinn.',
      'Fünf verstandene Kennzahlen mit Zielkorridor schlagen dreißig, die niemand liest.',
      'Daten aus Kasse, Warenwirtschaft, Personalplanung und Buchhaltung zusammenführen – wöchentlich, nicht jährlich.',
      'Zahlen gehören ins Team: Küche und Service können nur steuern, was sie kennen.',
    ],
    intro: 'Die meisten Gastronomen wissen am Monatsende, wie viel Umsatz sie gemacht haben – aber nicht, ob sie Geld verdient haben. Reporting und Controlling schließen diese Lücke. Dieser Leitfaden zeigt, welche Kennzahlen zählen, wie du sie aus deinen Systemen gewinnst und wie daraus Entscheidungen werden.',
    sections: [
      section(
        'Warum Bauchgefühl nicht mehr reicht',
        p('Steigende Wareneinkaufspreise, Personalkosten und Energiekosten haben die Margen in der Gastronomie verengt. Wer erst mit der Jahresauswertung des Steuerberaters erfährt, dass der Wareneinsatz bei 38 statt 30 Prozent lag, hat ein Jahr lang Geld verloren. Controlling bedeutet, diese Zahlen **wöchentlich oder täglich** zu sehen – solange du noch reagieren kannst.'),
        p('Die gute Nachricht: Die Daten sind längst da. Kasse, Warenwirtschaft, Personalplanung und Buchhaltung erzeugen sie ohnehin. Es geht darum, sie zusammenzuführen und lesbar zu machen.')
      ),
      section(
        'Die drei Ebenen des Controllings',
        table(
          ['Ebene', 'Rhythmus', 'Inhalt', 'Wer'],
          [
            ['Tagesreport', 'Täglich', 'Umsatz, Gäste, Durchschnittsbon, Umsatz nach Stunde, Zahlungsarten, Stornos', 'Schichtleitung, Inhaber'],
            ['Wochen-/Monatscontrolling', 'Wöchentlich / monatlich', 'Wareneinsatzquote, Personalkostenquote, Deckungsbeitrag, Renner-Penner, Vergleich Vorjahr', 'Inhaber, Küchenchef, Betriebsleitung'],
            ['Jahresplanung', 'Quartalsweise / jährlich', 'Budget, Investitionen, Preisstrategie, Kapazität, Liquidität', 'Inhaber, Steuerberater'],
          ]
        ),
        p('Der Tagesreport kommt meist direkt aus der Kasse. Für die Monatsebene müssen Daten aus mehreren Systemen zusammenfließen – hier entsteht der eigentliche Mehrwert.')
      ),
      section(
        'Die zentralen Kennzahlen',
        p('Du brauchst keine 50 Kennzahlen, sondern eine Handvoll, die du wirklich verstehst und regelmäßig anschaust:'),
        list([
          '**Umsatz pro Gast (Durchschnittsbon):** Der wichtigste Frühindikator für Verkaufsqualität und Preisgestaltung',
          '**Wareneinsatzquote:** Wareneinsatz ÷ Nettoumsatz – Richtwert je nach Konzept 25 – 35 %',
          '**Personalkostenquote:** Personalkosten ÷ Nettoumsatz – Richtwert 25 – 35 %',
          '**Deckungsbeitrag:** Was nach Wareneinsatz übrig bleibt – pro Gericht, pro Kategorie, pro Tag',
          '**Umsatz pro Sitzplatz und Öffnungsstunde:** Produktivität der Fläche',
          '**Renner-Penner-Analyse:** Welche Gerichte verkaufen sich gut und verdienen gut?',
          '**Stornoquote und Nachlässe:** Hinweis auf Prozess- oder Vertrauensprobleme',
          '**Liquidität:** Kontostand, offene Verbindlichkeiten, Gutscheinverbindlichkeiten',
        ]),
        p('Formeln, Richtwerte und Handlungsempfehlungen zu den zehn wichtigsten Kennzahlen findest du im gleichnamigen Vertiefungsartikel.')
      ),
      section(
        'Datenquellen zusammenführen',
        p('Jedes System liefert einen Teil des Bildes. Die Kunst ist, sie in einer Ansicht zu vereinen:'),
        table(
          ['System', 'Liefert', 'Für welche Kennzahl'],
          [
            ['Kasse', 'Umsatz nach Artikel, Stunde, Mitarbeiter, Zahlungsart; Gäste; Stornos', 'Umsatz, Bon, Renner-Penner, Stornoquote'],
            ['Warenwirtschaft', 'Einkauf, Bestände, Rezepturkosten', 'Wareneinsatz (Soll und Ist), Deckungsbeitrag je Gericht'],
            ['Personalplanung', 'Geleistete Stunden, Lohnkosten je Schicht', 'Personalkostenquote, Produktivität'],
            ['Reservierung', 'Gäste, Auslastung, No-Shows', 'Belegungsquote, No-Show-Rate'],
            ['Buchhaltung', 'Alle Kosten, Liquidität, Abgrenzungen', 'Ergebnis, Fixkostenquote, Cashflow'],
          ]
        ),
        p('Für den Einstieg reicht oft eine Tabellenkalkulation, in die wöchentlich Kassenumsatz, Wareneinkauf und Personalstunden eingetragen werden. Wer weiter ist, nutzt Dashboards, die Kassen-, Warenwirtschafts- und Personaldaten automatisch zusammenführen.')
      ),
      section(
        'Vom Reporting zur Entscheidung',
        p('Zahlen sind nur wertvoll, wenn sie zu Handlungen führen. Einige Beispiele aus der Praxis:'),
        list([
          '**Wareneinsatz steigt von 30 auf 34 %:** Preisliste der Lieferanten prüfen, Rezepturen kontrollieren, Inventurdifferenzen suchen, Portionsgrößen prüfen',
          '**Dienstagabend hat 42 % Personalkostenquote:** Besetzung anpassen oder gezielte Aktion für den Dienstag',
          '**Ein Gericht ist Renner mit niedrigem Deckungsbeitrag:** Preis anpassen oder Rezeptur optimieren',
          '**Durchschnittsbon sinkt trotz gleicher Gästezahl:** Verkaufsschulung, Getränkeempfehlung, Kartenstruktur überdenken',
          '**Stornoquote steigt bei einer Servicekraft:** Gespräch führen – Schulungsbedarf oder Prozessproblem',
        ]),
        tip('Definiere für jede Kennzahl einen Zielkorridor und eine Ampel: grün, gelb, rot. Rot bedeutet: Diese Woche wird gehandelt. Das macht Controlling zur Routine statt zur Analyseübung.')
      ),
      section(
        'Buchhaltungsschnittstelle und Steuerberater',
        p('Der Steuerberater ist dein Partner im Controlling – wenn er die Daten rechtzeitig und digital bekommt. Kassenabschlüsse, Belege und Ausgangsrechnungen sollten automatisiert übertragen werden, etwa über eine DATEV-Schnittstelle der Kasse und digitale Belegerfassung. So liegt die monatliche Auswertung wenige Tage nach Monatsende vor – nicht nach sechs Wochen. Wie die Schnittstelle funktioniert, zeigt der Vertiefungsartikel zur Buchhaltung.')
      ),
      section(
        'Kosten und Umsetzungsvarianten',
        table(
          ['Variante', 'Kosten', 'Geeignet für'],
          [
            ['Kassenreporting + Tabellenkalkulation', '0 € zusätzlich, ca. 1 Std. pro Woche', 'Einzelbetriebe zum Einstieg'],
            ['Reporting-Modul der Kasse oder Warenwirtschaft', 'ca. 20 – 100 € / Monat', 'Betriebe mit integrierten Systemen'],
            ['Dashboard-Lösung mit mehreren Datenquellen', 'ca. 50 – 300 € / Monat', 'Mehrere Standorte, Systemgastronomie'],
            ['Externes Controlling (Berater, Kanzlei)', 'ab ca. 200 € / Monat', 'Betriebe ohne eigene Zeit für Auswertung'],
          ]
        )
      ),
      section(
        'Einführung: Die Kennzahlen-Routine',
        steps([
          'Fünf Kennzahlen auswählen, die du wirklich verstehst',
          'Datenquellen klären: Woher kommt jede Zahl, wer trägt sie ein?',
          'Zielkorridore festlegen – aus Vorjahr, Branchenwerten und Kalkulation',
          'Festen Wochentermin (20 Minuten) für den Blick auf die Zahlen einrichten',
          'Monatlich mit Küchenchef und Schichtleitung besprechen – Zahlen gehören ins Team',
          'Nach drei Monaten prüfen: Welche Kennzahl hat zu Entscheidungen geführt? Welche kann weg?',
        ])
      ),
      section(
        'Häufige Denkfehler im Controlling',
        list([
          '**Brutto und netto vermischen:** Umsatz brutto, Kosten netto – Quoten stimmen nicht',
          '**Wareneinsatz nur aus Einkauf berechnen:** Ohne Inventur verzerren Bestandsänderungen das Bild',
          '**Inhaberarbeitszeit vergessen:** Die Personalkostenquote wirkt besser als sie ist',
          '**Zu viele Kennzahlen:** 30 Werte, die niemand liest, statt 5, die zu Handlungen führen',
          '**Zahlen nicht ins Team tragen:** Küche und Service können nur steuern, was sie kennen',
          '**Nur Umsatz feiern:** Rekordumsatz mit sinkender Marge ist kein Erfolg',
        ])
      ),
      section(
        'Fragen aus der Beratungspraxis',
        faq([
          { q: 'Reicht das Reporting meiner Kasse nicht aus?', a: 'Für Umsatz, Bon und Artikelstatistik ja. Wareneinsatz, Personalkosten und Deckungsbeitrag brauchen aber Daten aus anderen Systemen. Ohne diese Verbindung siehst du nur die Einnahmenseite.' },
          { q: 'Wie oft sollte ich Inventur machen, um den Wareneinsatz korrekt zu berechnen?', a: 'Monatlich ist der Standard für aussagekräftige Monatszahlen. Getränke und hochpreisige Waren können wöchentlich gezählt werden. Ohne Inventur ist die Wareneinsatzquote nur eine Einkaufsquote.' },
          { q: 'Was ist ein guter Deckungsbeitrag für ein Gericht?', a: 'Das hängt vom Konzept ab. Als Faustregel: Der Deckungsbeitrag in Euro ist wichtiger als die Quote – ein Gericht mit 35 % Wareneinsatz und 12 € Deckungsbeitrag ist besser als eines mit 25 % und 6 €. Die Menü-Engineering-Analyse im Vertiefungsartikel hilft bei der Einordnung.' },
          { q: 'Muss ich meine Zahlen mit dem Team teilen?', a: 'Nicht alle, aber die steuerungsrelevanten: Wareneinsatz und Stornoquote für die Küche, Durchschnittsbon und Getränkeanteil für den Service. Wer Zahlen kennt, kann sie beeinflussen – und Ziele werden greifbar.' },
        ])
      ),
    ],
  },
  subArticles: [
    {
      slug: 'wichtigste-kennzahlen-gastronomie',
      title: 'Die 10 wichtigsten Kennzahlen in der Gastronomie – mit Formeln und Richtwerten',
      shortTitle: 'Die 10 wichtigsten Kennzahlen',
      teaser: 'Von Durchschnittsbon bis Liquidität: Jede Kennzahl mit Formel, Richtwert und der Frage, welche Handlung sie auslösen sollte.',
      intro: 'Kennzahlen sind nur so gut wie die Entscheidungen, die sie auslösen. Diese zehn haben sich in der Praxis als die wirkungsvollsten erwiesen – für jede findest du Formel, Richtwert und den typischen Handlungsimpuls.',
      sections: [
        section(
          'Umsatz- und Gästekennzahlen',
          table(
            ['Kennzahl', 'Formel', 'Richtwert', 'Handlung bei Abweichung'],
            [
              ['1. Durchschnittsbon', 'Nettoumsatz ÷ Anzahl Gäste (oder Bons)', 'Konzeptabhängig; Trend beobachten', 'Verkaufsschulung, Kartenstruktur, Empfehlungen'],
              ['2. Getränkeanteil', 'Getränkeumsatz ÷ Gesamtumsatz', '30 – 40 % im Restaurant', 'Getränkeempfehlung, Karte, Aperitif-Angebot'],
              ['3. Umsatz je Sitzplatz-Stunde', 'Umsatz ÷ (Sitzplätze × Öffnungsstunden)', 'Vergleich zwischen Tagen und Zeiten', 'Öffnungszeiten, Sitzzeiten, Aktionen für schwache Zeiten'],
              ['4. Belegungsquote', 'Belegte Plätze ÷ verfügbare Plätze je Zeitfenster', 'Abendspitze 80 – 95 %', 'Reservierungssteuerung, Marketing für schwache Slots'],
            ]
          )
        ),
        section(
          'Kostenkennzahlen',
          table(
            ['Kennzahl', 'Formel', 'Richtwert', 'Handlung bei Abweichung'],
            [
              ['5. Wareneinsatzquote', '(Anfangsbestand + Einkauf − Endbestand) ÷ Nettoumsatz', '25 – 35 %', 'Rezepturen, Lieferantenpreise, Schwund, Portionen prüfen'],
              ['6. Personalkostenquote', 'Personalkosten inkl. Inhaber ÷ Nettoumsatz', '25 – 35 %', 'Schichtplanung nach Prognose, Produktivität je Bereich'],
              ['7. Prime Cost', 'Wareneinsatz + Personalkosten ÷ Nettoumsatz', 'Unter 60 – 65 %', 'Wichtigste Gesamtkennzahl – wenn sie kippt, kippt der Betrieb'],
              ['8. Fixkostenquote', 'Miete, Energie, Versicherung, Abos ÷ Nettoumsatz', 'Unter 20 – 25 %', 'Verträge prüfen, Umsatzbasis verbreitern'],
            ]
          )
        ),
        section(
          'Ergebnis- und Steuerungskennzahlen',
          table(
            ['Kennzahl', 'Formel', 'Richtwert', 'Handlung bei Abweichung'],
            [
              ['9. Deckungsbeitrag je Gericht', 'Verkaufspreis netto − Wareneinsatz', 'In Euro betrachten, nicht nur in Prozent', 'Menü-Engineering: Preis, Platzierung, Rezeptur'],
              ['10. Stornoquote', 'Stornierter Umsatz ÷ Bruttoumsatz', 'Unter 1 – 2 %', 'Prozesse, Schulung, Berechtigungen prüfen'],
            ]
          ),
          p('Ergänzend solltest du die **Liquidität** im Blick behalten: Kontostand plus erwartete Einnahmen minus fällige Zahlungen der nächsten vier Wochen. Sie ist keine Verhältniskennzahl, aber die, an der Betriebe am häufigsten scheitern – auch profitable.')
        ),
        section(
          'Kennzahlen richtig lesen',
          list([
            'Immer netto rechnen – Umsatz ohne Mehrwertsteuer, Kosten ohne Vorsteuer',
            'Trends sind wichtiger als Einzelwerte: Vergleiche mit Vorwoche, Vormonat, Vorjahr',
            'Kennzahlen nach Bereich trennen (Küche/Service, Speisen/Getränke, Tageszeiten)',
            'Richtwerte sind Orientierung – dein Konzept bestimmt den Zielkorridor',
            'Eine Kennzahl allein erklärt nichts: Steigender Bon bei sinkender Gästezahl kann ein Warnsignal sein',
          ]),
          tip('Baue eine einseitige Wochenübersicht mit diesen zehn Werten, jeweils mit Vorwoche und Ziel. Wer sie jeden Montag anschaut, erkennt Probleme Wochen bevor sie in der Buchhaltung sichtbar werden.')
        ),
      ],
    },
    {
      slug: 'wareneinsatz-deckungsbeitrag',
      title: 'Wareneinsatz und Deckungsbeitrag richtig berechnen – inklusive Menü-Engineering',
      shortTitle: 'Wareneinsatz & Deckungsbeitrag',
      teaser: 'Soll- und Ist-Wareneinsatz, Deckungsbeitrag pro Gericht und die Renner-Penner-Matrix: So findest du heraus, welche Gerichte Geld verdienen und welche nur Arbeit machen.',
      intro: 'Der Wareneinsatz ist der Kostenblock, den du am schnellsten beeinflussen kannst – wenn du ihn richtig misst. Und der Deckungsbeitrag pro Gericht zeigt, wo deine Karte Geld verdient und wo nicht.',
      sections: [
        section(
          'Wareneinsatz: Einkauf ist nicht gleich Verbrauch',
          p('Viele Betriebe berechnen den Wareneinsatz aus den Einkaufsrechnungen des Monats. Das ist falsch, sobald Bestände schwanken – ein Großeinkauf vor dem Feiertag verzerrt die Quote. Die korrekte Formel berücksichtigt die Bestandsveränderung:'),
          p('**Ist-Wareneinsatz = Anfangsbestand + Einkäufe − Endbestand**'),
          p('Dafür brauchst du eine Inventur zu Monatsbeginn und -ende. Der Ist-Wareneinsatz geteilt durch den Nettoumsatz ergibt die Wareneinsatzquote.')
        ),
        section(
          'Soll-Wareneinsatz: Was hätte es kosten dürfen?',
          p('Der Soll-Wareneinsatz ergibt sich aus den Rezepturen: Für jedes verkaufte Gericht (Kassendaten) wird der kalkulierte Wareneinsatz laut Rezeptur addiert. Die Differenz zwischen Soll und Ist ist die interessanteste Zahl im Controlling:'),
          table(
            ['', 'Beispielmonat'],
            [
              ['Nettoumsatz Speisen', '60.000 €'],
              ['Soll-Wareneinsatz (aus Rezepturen × Verkäufe)', '17.400 € (29,0 %)'],
              ['Ist-Wareneinsatz (aus Inventur)', '19.800 € (33,0 %)'],
              ['**Differenz**', '**2.400 € (4 Prozentpunkte)**'],
            ]
          ),
          p('2.400 € im Monat, die nicht durch Verkäufe erklärt sind: Verderb, Schwund, Personalverpflegung, Überportionierung, Rezepturabweichungen, fehlerhafte Buchungen. Erst diese Differenz macht Wareneinsatzprobleme greifbar.')
        ),
        section(
          'Deckungsbeitrag pro Gericht',
          p('Der Deckungsbeitrag ist der Verkaufspreis netto minus Wareneinsatz. Er zeigt, wie viel ein Gericht zur Deckung von Personal, Miete und Gewinn beiträgt. Wichtig: **Euro schlagen Prozent.**'),
          table(
            ['Gericht', 'Verkaufspreis netto', 'Wareneinsatz', 'WE-Quote', 'Deckungsbeitrag'],
            [
              ['Pasta mit Tomatensauce', '11,21 €', '2,20 €', '19,6 %', '9,01 €'],
              ['Rinderfilet', '30,84 €', '13,50 €', '43,8 %', '17,34 €'],
              ['Caesar Salad', '12,15 €', '3,40 €', '28,0 %', '8,75 €'],
            ]
          ),
          p('Das Filet hat die schlechteste Quote – und den höchsten Deckungsbeitrag. Wer nur auf Prozente schaut, würde es von der Karte nehmen und Geld verlieren.')
        ),
        section(
          'Menü-Engineering: Die Renner-Penner-Matrix',
          p('Kombiniere Verkaufsmenge (aus der Kasse) mit Deckungsbeitrag (aus der Kalkulation) für jedes Gericht. Vergleiche beide Werte mit dem Durchschnitt deiner Karte – es entstehen vier Gruppen:'),
          table(
            ['Gruppe', 'Verkauf', 'Deckungsbeitrag', 'Strategie'],
            [
              ['Stars (Gewinner)', 'Hoch', 'Hoch', 'Prominent platzieren, Qualität halten, nicht verändern'],
              ['Renner', 'Hoch', 'Niedrig', 'Preis vorsichtig anheben, Rezeptur optimieren, Beilagen anpassen'],
              ['Schläfer', 'Niedrig', 'Hoch', 'Besser platzieren, empfehlen lassen, umbenennen, Foto'],
              ['Penner', 'Niedrig', 'Niedrig', 'Von der Karte nehmen oder komplett überarbeiten'],
            ]
          ),
          tip('Führe die Analyse quartalsweise oder zu jedem Kartenwechsel durch. Schon zwei bis drei gezielte Änderungen – ein Preisschritt bei einem Renner, ein besserer Platz für einen Schläfer – bringen oft mehrere Tausend Euro Deckungsbeitrag im Jahr.')
        ),
        section(
          'Maßnahmen bei zu hohem Wareneinsatz',
          list([
            'Soll-Ist-Differenz analysieren: Wo entsteht sie – Küche, Bar, Lager?',
            'Rezepturen mit aktuellen Einkaufspreisen neu kalkulieren',
            'Portionsgrößen mit Waage prüfen – Abweichungen von 10 % sind üblich',
            'Verderb erfassen und auswerten (Abschreibungsliste)',
            'Lieferantenpreise vergleichen und verhandeln',
            'Karte verkleinern: Weniger Artikel, weniger Bestände, weniger Verderb',
          ])
        ),
      ],
    },
    {
      slug: 'buchhaltung-schnittstelle-datev',
      title: 'Schnittstelle zur Buchhaltung: Kasse, DATEV und Steuerberater digital verbinden',
      shortTitle: 'Buchhaltung & DATEV',
      teaser: 'Was dein Steuerberater braucht, wie Kassendaten und Belege automatisch in die Buchhaltung gelangen und wie du die Monatsauswertung in Tagen statt Wochen bekommst.',
      intro: 'Der Pendelordner mit Kassenbons und Rechnungen ist der Flaschenhals des Controllings. Wer Kasse, Belege und Steuerberater digital verbindet, bekommt aktuelle Zahlen – und spart jeden Monat Stunden.',
      sections: [
        section(
          'Was der Steuerberater braucht',
          list([
            '**Kassenabschlüsse (Z-Bons)** mit Umsätzen nach Steuersätzen und Zahlungsarten',
            '**Kassenbuch** bei Bargeldbewegungen (Einlagen, Entnahmen, Ausgaben aus der Kasse)',
            '**Eingangsrechnungen** (Lieferanten, Miete, Energie, Abos)',
            '**Ausgangsrechnungen** (Firmenkunden, Events, Catering)',
            '**Kontoauszüge** – meist per Bankschnittstelle automatisch',
            '**Lohndaten** aus der Personalplanung',
            '**Gutscheinverbindlichkeiten** und deren Veränderung',
          ])
        ),
        section(
          'Der digitale Belegfluss',
          p('Ziel ist, dass kein Beleg mehr physisch transportiert oder abgetippt wird:'),
          steps([
            '**Kasse → Buchhaltung:** Tagesabschlüsse werden automatisch im DATEV-Format oder über eine direkte Schnittstelle übertragen – inklusive Aufteilung nach Steuersätzen und Zahlungsarten.',
            '**Eingangsrechnungen digital:** Lieferanten senden PDF-Rechnungen an eine zentrale E-Mail-Adresse; Papierbelege werden per App fotografiert. Eine Belegerkennung liest Betrag, Datum und Lieferant aus.',
            '**Zahlungsabgleich:** Bankumsätze werden per Schnittstelle abgerufen und automatisch Belegen zugeordnet.',
            '**Freigabe und Übergabe:** Du prüfst offene Belege in einer Übersicht; die Kanzlei erhält alles gebündelt zum Monatswechsel.',
          ]),
          tip('E-Rechnungen im strukturierten Format werden im Geschäftsverkehr zunehmend Standard. Achte darauf, dass deine Belegverarbeitung diese Formate empfangen und weiterleiten kann – das erspart zukünftige Umstellungen.')
        ),
        section(
          'Die Kassenschnittstelle im Detail',
          p('Die meisten Kassensysteme bieten einen Export für die gängige Kanzleisoftware – entweder als Datei zum Hochladen oder als direkte Anbindung. Wichtig ist die richtige Konfiguration:'),
          list([
            'Kontenrahmen mit dem Steuerberater abstimmen (Erlöskonten nach Steuersatz, Zahlungsarten, Trinkgeld, Gutscheine)',
            'Trinkgeld getrennt vom Umsatz erfassen',
            'Gutscheinverkauf als Verbindlichkeit, Einlösung als Umsatz',
            'Kartenzahlungen mit Auszahlungen des Zahlungsanbieters abgleichbar machen',
            'Bargeldbewegungen (Einlagen, Entnahmen, Ausgaben) sauber im Kassenbuch',
          ])
        ),
        section(
          'Die Monatsabschluss-Routine',
          table(
            ['Wann', 'Was', 'Wer'],
            [
              ['Täglich', 'Tagesabschluss in der Kasse, Bargeld zählen, Belege fotografieren', 'Schichtleitung'],
              ['Wöchentlich', 'Offene Belege prüfen und zuordnen, Kassenbuch kontrollieren', 'Inhaber / Büro'],
              ['1. – 3. des Monats', 'Inventur erfassen, Gutscheinliste aktualisieren, Belege komplett', 'Küche / Büro'],
              ['Bis 5. des Monats', 'Übergabe an Kanzlei, Rückfragen klären', 'Büro'],
              ['Bis 15. des Monats', 'Betriebswirtschaftliche Auswertung liegt vor, Besprechung', 'Inhaber + Steuerberater'],
            ]
          )
        ),
        section(
          'Vorteile und Checkliste',
          p('Der digitale Belegfluss spart je nach Betriebsgröße vier bis zehn Stunden im Monat, senkt Kanzleikosten für Belegerfassung und liefert Auswertungen, mit denen du noch im laufenden Monat steuern kannst.'),
          steps([
            'Mit dem Steuerberater klären, welche Formate und Schnittstellen die Kanzlei nutzt',
            'Kassenschnittstelle einrichten und Kontenzuordnung abstimmen',
            'Zentrale Rechnungs-E-Mail einrichten und Lieferanten informieren',
            'Beleg-App für Barbelege einführen, Team schulen',
            'Bankschnittstelle aktivieren',
            'Ersten Monat gemeinsam mit der Kanzlei durchgehen und Regeln nachschärfen',
          ])
        ),
      ],
    },
  ],
};
